// src/pages/OrderOnTheWay/components/OtpVerificationModal.jsx
import React, { useState } from 'react';
import { verifyPaymentOtp } from '../services/paymentService';

const OtpVerificationModal = ({ modalData, onClose, onVerify, verifyFn, resendFn }) => {
  const [otpInput, setOtpInput] = useState('');
  const [otpError, setOtpError] = useState('');
  const [loading, setLoading] = useState(false);
  // A resend issues a fresh otpId — track it so verification uses the latest
  const [currentOtpId, setCurrentOtpId] = useState(modalData?.otpId);
  const [currentDisplayOtp, setCurrentDisplayOtp] = useState(modalData?.displayOtp || null);
  const [resending, setResending] = useState(false);
  const [resendInfo, setResendInfo] = useState('');

  const handleResend = async () => {
    if (!resendFn || resending || loading) return;
    setResending(true);
    setOtpError('');
    setResendInfo('');
    try {
      const data = await resendFn({ ...modalData, otpId: currentOtpId });
      if (data?.otpId) setCurrentOtpId(data.otpId);
      if (data?.displayOtp !== undefined) setCurrentDisplayOtp(data.displayOtp || null);
      setOtpInput('');
      setResendInfo('New OTP sent to the JRE’s registered mobile number.');
    } catch (error) {
      setOtpError(error.message || 'Failed to resend OTP.');
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async () => {
    if (otpInput.length !== 6 || !/^\d{6}$/.test(otpInput)) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setOtpError('');

    try {
      // verifyFn lets callers plug in a different handover verifier
      // (e.g. seller payment handover for PRE_PICKUP / WAREHOUSE flows).
      const payload = { ...modalData, otpId: currentOtpId || modalData.otpId };
      const res = await (verifyFn || verifyPaymentOtp)(otpInput, payload);
      if (res && res.success) {
        onVerify(`✅ Payment of ₹${modalData.totalPayment} handed over to JRE ${modalData.jreId}. Settlement ID: ${res.settlementId}`);
        onClose();
      } else {
        setOtpError('OTP verification failed. Please try again.');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setOtpError(error.message || 'OTP verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpInput(value);
    if (otpError) setOtpError('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && otpInput.length === 6) {
      handleVerify();
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Verify Payment Handover to JRE</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="delivery-info">
            <div className="info-row">
              <span className="label">Operator:</span>
              <span className="value">{modalData.operatorId || '—'}</span>
            </div>
            <div className="info-row">
              <span className="label">Assigned JRE:</span>
              <span className="value">{modalData.jreId}</span>
            </div>
            {modalData.mobile && (
              <div className="info-row">
                <span className="label">JRE Mobile:</span>
                <span className="value">+91 {modalData.mobile}</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Seller:</span>
              <span className="value">{modalData.seller}</span>
            </div>
            <div className="info-row">
              <span className="label">Total Amount:</span>
              <span className="value amount">₹{parseFloat(modalData.totalPayment || 0).toFixed(2)}</span>
            </div>
            <div className="info-row">
              <span className="label">Orders:</span>
              <span className="value">{modalData.orderCount}</span>
            </div>
          </div>

          {currentDisplayOtp && (
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                padding: "8px 12px",
                borderRadius: "6px",
                fontSize: "13px",
                color: "#166534",
                marginBottom: "1rem",
                textAlign: "center"
              }}
            >
              <strong>[DEV TEST OTP]:</strong> {currentDisplayOtp}
            </div>
          )}

          <div className="otp-input-section">
            <label htmlFor="otp-input">Enter OTP sent to JRE (+91 {modalData.mobile ? modalData.mobile.slice(-4).padStart(modalData.mobile.length, '*') : '****'})</label>
            <input
              id="otp-input"
              type="text"
              value={otpInput}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Enter 6-digit OTP"
              maxLength={6}
              className="otp-input"
              disabled={loading}
              autoFocus
            />
            {otpError && (
              <div className="error-message">
                ⚠️ {otpError}
              </div>
            )}
          </div>

          <div className="otp-help">
            <p>📱 OTP has been sent to JRE's registered mobile number</p>
            <p>⏱️ OTP is valid for 10 minutes</p>
            {resendInfo && (
              <p style={{ color: "#166534", margin: "4px 0 0" }}>{resendInfo}</p>
            )}
            {resendFn && (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending || loading}
                style={{
                  marginTop: "6px",
                  background: "none",
                  border: "1px solid #93c5fd",
                  color: "#2563eb",
                  borderRadius: "6px",
                  padding: "6px 12px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: resending || loading ? "not-allowed" : "pointer",
                }}
              >
                {resending ? "Resending…" : "Resend OTP"}
              </button>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button
            onClick={onClose}
            disabled={loading}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleVerify}
            disabled={loading || otpInput.length !== 6}
            className="btn-primary"
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Verifying...
              </>
            ) : (
              'Verify & Handover'
            )}
          </button>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 450px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }

        .modal-header h3 {
          margin: 0;
          color: #1f2937;
          font-size: 18px;
          font-weight: 600;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #6b7280;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
        }

        .close-button:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .modal-body {
          padding: 24px;
        }

        .delivery-info {
          background: #f8fafc;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .info-row:last-child {
          margin-bottom: 0;
        }

        .label {
          color: #6b7280;
          font-weight: 500;
        }

        .value {
          color: #1f2937;
          font-weight: 600;
        }

        .value.amount {
          color: #059669;
          font-size: 16px;
        }

        .otp-input-section {
          margin-bottom: 16px;
        }

        .otp-input-section label {
          display: block;
          margin-bottom: 8px;
          color: #374151;
          font-weight: 500;
        }

        .otp-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #d1d5db;
          border-radius: 8px;
          font-size: 18px;
          text-align: center;
          letter-spacing: 8px;
          font-weight: 600;
          transition: border-color 0.2s;
        }

        .otp-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .otp-input:disabled {
          background-color: #f3f4f6;
          cursor: not-allowed;
        }

        .error-message {
          color: #dc2626;
          font-size: 14px;
          margin-top: 8px;
          padding: 8px 12px;
          background: #fef2f2;
          border-radius: 4px;
          border: 1px solid #fecaca;
        }

        .otp-help {
          background: #eff6ff;
          border-radius: 6px;
          padding: 12px 16px;
          font-size: 13px;
          color: #374151;
        }

        .otp-help p {
          margin: 4px 0;
        }

        .modal-footer {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          padding: 20px 24px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .btn-secondary {
          padding: 10px 20px;
          border: 1px solid #d1d5db;
          background: white;
          color: #374151;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #f3f4f6;
        }

        .btn-secondary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          padding: 10px 20px;
          border: none;
          background: #3b82f6;
          color: white;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .btn-primary:hover:not(:disabled) {
          background: #2563eb;
        }

        .btn-primary:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Responsive Design */
        @media (max-width: 480px) {
          .modal-overlay {
            padding: 10px;
          }

          .modal-content {
            max-width: 100%;
          }

          .modal-body {
            padding: 16px;
          }

          .modal-footer {
            flex-direction: column-reverse;
          }

          .btn-secondary, .btn-primary {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default OtpVerificationModal;